// The two sales events sent to Meta (owner, 2026-10-18), decided in plain
// functions so the rules are testable. Nothing here changes registration,
// sign-in, billing or any subscription behavior: it only READS what the account
// page has already loaded (rb_site_my_billing) and reports a fact once.
//
// CompleteRegistration — an account that now has its 30 free days.
//   · Not when the sign-up form is submitted: that email may never be
//     confirmed, and a throwaway address is refused its free month (migration
//     20261013000007). It fires on the first visit AFTER the email is
//     confirmed, when the database itself says level 'trial'.
//   · "Just confirmed" = email_confirmed_at within the last 30 minutes, so a
//     trial user opening the site on a second device later is not a second
//     registration. Once per account per browser.
//
// Subscribe — a paid plan that Stripe has confirmed.
//   · Only on the return from Checkout (?checkout=success) AND when the
//     database shows the subscription as 'active'. There is no Stripe-side
//     trial (RAISEDBEEF_COM_BILLING_PLAN.md), so 'active' means a payment went
//     through. Never the checkout click, never 'past_due', never a free plan
//     from /manage/subscribers.
//   · Once per subscription: the key includes the period end, so reloading the
//     success page does not count it again, and a later re-subscribe does.

import { supabase } from "@/integrations/supabase/client";
import { trackMetaEvent } from "@/lib/metaPixel";
import { SITE_PLAN } from "@/lib/sitePlan";

export const JUST_CONFIRMED_MS = 30 * 60 * 1000;

export interface BillingFacts {
  level: string;
  tier: "basic" | "premium" | null;
  interval: "monthly" | "annual" | null;
  status: string | null;
  current_period_end: string | null;
}

export function shouldReportRegistration(
  level: string,
  emailConfirmedAt: string | null | undefined,
  now: number,
  alreadySent: boolean,
): boolean {
  if (alreadySent || level !== "trial" || !emailConfirmedAt) return false;
  const at = Date.parse(emailConfirmedAt);
  return Number.isFinite(at) && now - at >= 0 && now - at <= JUST_CONFIRMED_MS;
}

export function shouldReportSubscribe(b: BillingFacts, returnedFromCheckout: boolean, alreadySent: boolean): boolean {
  return returnedFromCheckout && !alreadySent && b.status === "active" && !!b.tier;
}

/** Identifies one subscription; the user id keeps two accounts on one browser apart. */
export function subscribeKey(userId: string, b: BillingFacts): string {
  return [userId, b.tier, b.interval, b.current_period_end ?? ""].join("|");
}

function read(key: string): string | null {
  try { return window.localStorage.getItem(key); } catch { return null; }
}
function write(key: string, value: string): void {
  try { window.localStorage.setItem(key, value); } catch { /* an unwritable flag only risks a repeat */ }
}

/** Called with each answer from rb_site_my_billing. Never throws, never blocks. */
export async function reportBillingToMeta(b: BillingFacts, price: number, returnedFromCheckout: boolean): Promise<void> {
  try {
    const { data } = await supabase.auth.getSession();
    const user = data.session?.user;
    if (!user) return;

    const regKey = `rb_meta_reg_${user.id}`;
    if (shouldReportRegistration(b.level, user.email_confirmed_at, Date.now(), read(regKey) === "1")) {
      const sent = trackMetaEvent("CompleteRegistration", { content_name: "30-day free trial", status: true }, `reg-${user.id}`);
      if (sent) write(regKey, "1");
    }

    const subKey = subscribeKey(user.id, b);
    if (shouldReportSubscribe(b, returnedFromCheckout, read("rb_meta_sub") === subKey) && price > 0) {
      const sent = trackMetaEvent(
        "Subscribe",
        { value: price, currency: "USD", content_name: SITE_PLAN.name, plan: "member", interval: b.interval ?? "monthly" },
        `sub-${subKey}`,
      );
      if (sent) write("rb_meta_sub", subKey);
    }
  } catch {
    // Advertising measurement must never break the account page.
  }
}
